import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { casesApi, CreateCaseRequest } from '../../services/cases';
import { clientsApi } from '../../services/clients';
import { Client, CaseStatus } from '../../types';

export const CaseForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<CreateCaseRequest>({
    clientId: clientIdParam || '',
    childFirstName: '',
    childLastName: '',
    childDOB: '',
    schoolDistrict: '',
    school: '',
    grade: '',
    disabilities: [],
    services: [],
    notes: '',
    status: 'active',
  });

  useEffect(() => {
    loadClients();
    if (isEdit && id) loadCase(id);
  }, [id, isEdit]);

  const loadClients = async () => {
    try {
      const data = await clientsApi.list();
      setClients(data.clients);
    } catch (err) {
      console.error('Failed to load clients');
    }
  };

  const loadCase = async (caseId: string) => {
    try {
      setLoading(true);
      const caseData = await casesApi.get(caseId);
      setForm({
        clientId: caseData.clientId,
        childFirstName: caseData.childFirstName,
        childLastName: caseData.childLastName,
        childDOB: caseData.childDOB || '',
        schoolDistrict: caseData.schoolDistrict || '',
        school: caseData.school || '',
        grade: caseData.grade || '',
        disabilities: caseData.disabilities || [],
        services: caseData.services || [],
        notes: caseData.notes || '',
        status: caseData.status,
      });
    } catch (err) {
      setError('Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.clientId || !form.childFirstName.trim() || !form.childLastName.trim()) {
      setError('Client, child first name, and child last name are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      if (isEdit && id) {
        const { clientId, ...updateData } = form;
        await casesApi.update(id, updateData);
      } else {
        await casesApi.create(form);
      }
      navigate(clientIdParam ? `/clients/${clientIdParam}` : '/cases');
    } catch (err) {
      setError('Failed to save case');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' };
  const labelStyle = { display: 'block', marginBottom: '4px', fontWeight: 500 as const, color: '#333' };

  if (loading) return <Layout><div style={{ padding: '24px' }}>Loading...</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: '24px', maxWidth: '600px' }}>
        <h1>{isEdit ? 'Edit Case' : 'New Case'}</h1>
        {error && <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Client (Parent) *</label>
              <select
                style={inputStyle}
                value={form.clientId}
                onChange={e => setForm({ ...form, clientId: e.target.value })}
                disabled={isEdit || Boolean(clientIdParam)}
                required
              >
                <option value="">Select a client...</option>
                {clients.map(c => (
                  <option key={c.clientId} value={c.clientId}>{c.lastName}, {c.firstName}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Child First Name *</label>
                <input style={inputStyle} value={form.childFirstName} onChange={e => setForm({ ...form, childFirstName: e.target.value })} required />
              </div>
              <div>
                <label style={labelStyle}>Child Last Name *</label>
                <input style={inputStyle} value={form.childLastName} onChange={e => setForm({ ...form, childLastName: e.target.value })} required />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Date of Birth</label>
                <input style={inputStyle} type="date" value={form.childDOB} onChange={e => setForm({ ...form, childDOB: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Grade</label>
                <input style={inputStyle} value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} placeholder="e.g., 5th" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>School District</label>
                <input style={inputStyle} value={form.schoolDistrict} onChange={e => setForm({ ...form, schoolDistrict: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>School</label>
                <input style={inputStyle} value={form.school} onChange={e => setForm({ ...form, school: e.target.value })} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value as CaseStatus })}>
                <option value="active">Active</option>
                <option value="on-hold">On Hold</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button type="submit" disabled={saving} style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : (isEdit ? 'Update Case' : 'Create Case')}
              </button>
              <button type="button" onClick={() => navigate(-1)} style={{ backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', padding: '12px 24px', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};
